/**
 * Security tab — change password (Phase 11-D) + honest placeholders for MFA/API.
 */
import {
	Alert,
	Box,
	Button,
	Chip,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import ApiIcon from "@mui/icons-material/Api";
import DevicesIcon from "@mui/icons-material/Devices";
import LockIcon from "@mui/icons-material/Lock";
import { useState } from "react";

import { useAuth } from "../../auth";
import { GlassSurface } from "../../layouts/components/GlassSurface";
import { getApiClient } from "../../services/apiClientProvider";
import { SpecialNote } from "../results/components/SpecialNote";

export function SecurityPanel() {
	const { actions } = useAuth();
	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirm, setConfirm] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState(false);

	const handleChangePassword = async () => {
		setError(null);
		setSuccess(false);
		if (newPassword !== confirm) {
			setError("New password and confirmation do not match.");
			return;
		}
		setBusy(true);
		try {
			await getApiClient().changePassword({
				currentPassword,
				newPassword,
			});
			setCurrentPassword("");
			setNewPassword("");
			setConfirm("");
			setSuccess(true);
			// Server revokes refresh families — force re-login for clean session.
			await actions.logout();
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
		} finally {
			setBusy(false);
		}
	};

	return (
		<Box data-testid="account-security-panel">
			<GlassSurface intensity="subtle" depth="flat" sx={{ p: 2.5, mb: 2 }}>
				<Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
					<LockIcon sx={{ color: "workspace.textMuted" }} aria-hidden />
					<Typography variant="subtitle1" fontWeight={700}>
						Change password
					</Typography>
				</Stack>
				<Typography variant="body2" sx={{ color: "workspace.textSecondary", mb: 2 }}>
					Updating your password signs out all other sessions. You will need to
					sign in again on this device.
				</Typography>
				{error && (
					<Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
						{error}
					</Alert>
				)}
				{success && (
					<Alert severity="success" sx={{ mb: 2 }}>
						Password updated. Please sign in again.
					</Alert>
				)}
				<Stack spacing={1.5} sx={{ maxWidth: 420 }}>
					<TextField
						type="password"
						label="Current password"
						value={currentPassword}
						onChange={(e) => setCurrentPassword(e.target.value)}
						autoComplete="current-password"
						size="small"
						fullWidth
					/>
					<TextField
						type="password"
						label="New password"
						value={newPassword}
						onChange={(e) => setNewPassword(e.target.value)}
						autoComplete="new-password"
						size="small"
						fullWidth
						helperText="At least 12 characters, with a letter and a digit."
					/>
					<TextField
						type="password"
						label="Confirm new password"
						value={confirm}
						onChange={(e) => setConfirm(e.target.value)}
						autoComplete="new-password"
						size="small"
						fullWidth
					/>
					<Button
						variant="contained"
						disabled={
							busy ||
							!currentPassword ||
							!newPassword ||
							!confirm
						}
						onClick={() => void handleChangePassword()}
						sx={{ alignSelf: "flex-start" }}
					>
						{busy ? "Updating…" : "Update password"}
					</Button>
				</Stack>
			</GlassSurface>

			<SpecialNote kind="guidance" title="More security controls" dense>
				Multi-factor authentication, device session lists, and personal API
				tokens are planned for a later release.
			</SpecialNote>

			<Box
				sx={{
					display: "grid",
					gap: 2,
					gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
					mt: 2,
				}}
			>
				{[
					{
						title: "Sessions & devices",
						detail: "Review signed-in sessions and revoke access on other devices.",
						icon: DevicesIcon,
					},
					{
						title: "API access",
						detail: "Personal access tokens, rate limits, and audit logs for integrations.",
						icon: ApiIcon,
					},
				].map((item) => {
					const Icon = item.icon;
					return (
						<GlassSurface
							key={item.title}
							intensity="subtle"
							depth="flat"
							sx={{ p: 2.25, height: "100%" }}
						>
							<Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1 }}>
								<Icon sx={{ color: "workspace.textMuted" }} aria-hidden />
								<Typography variant="subtitle1" fontWeight={700}>
									{item.title}
								</Typography>
							</Stack>
							<Chip
								label="Available in future release"
								size="small"
								variant="outlined"
								sx={{
									mb: 1,
									borderColor: "workspace.borderStrong",
									color: "workspace.textMuted",
								}}
							/>
							<Typography variant="body2" sx={{ color: "workspace.textSecondary" }}>
								{item.detail}
							</Typography>
						</GlassSurface>
					);
				})}
			</Box>
		</Box>
	);
}
